import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {ExperimentNames, getFormMessages, isAssignedTickets} from '@wix/wix-events-commons-statics'
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import React, {useEffect, useRef} from 'react'
import {isTemplate} from '../../../../../commons/selectors/instance'
import {CHECKOUT_STEPS, FormStep} from '../../constants/constants'
import {useAppActions, useAppSelector} from '../../hooks/runtime'
import {
  getBuyerDetails,
  getCurrentStep,
  isAllTicketDetailsValid,
  isStepCompleted,
  isStepVisible,
  isValidPaymentAdded,
} from '../../selectors/checkout'
import {getEvent} from '../../selectors/event'
import {getGiftCardPaymentDetails} from '../../selectors/invoice'
import {isEventPreview} from '../../selectors/navigation'
import {getInvoice, getPlacedOrder} from '../../selectors/placed-order'
import {hasAgreedWithPolicies, hasPolicies} from '../../selectors/policies'
import {BuyerDetails} from '../buyer-details'
import {StatusScreensEntry} from '../chunks/status-screens-entry'
import {Policies} from '../policies'
import {CheckoutProps} from './interfaces'
import {Payment} from './payment'
import {PaymentWidget} from './payment/payment-widget'
import {TicketsDetails} from './tickets-details'

export const Checkout = ({isMobile, children}: CheckoutProps) => {
  const {experiments} = useExperiments()
  const {t} = useTranslation()
  const {
    editStep,
    setExpandedTicketIndex,
    submitCheckoutStep,
    setValidPaymentAdded,
    paymentMethodSelected,
    handleNextStep,
    placeOrderButtonClicked,
  } = useAppActions()
  const payment = useRef<PaymentWidget>(null)

  const placedOrder = useAppSelector(getPlacedOrder)
  const invoice = useAppSelector(getInvoice)
  const buyerDetails = useAppSelector(getBuyerDetails)
  const currentStep = useAppSelector(getCurrentStep)
  const allTicketDetailsValid = useAppSelector(isAllTicketDetailsValid)
  const validPaymentAdded = useAppSelector(isValidPaymentAdded)
  const giftCardPaymentDetails = useAppSelector(getGiftCardPaymentDetails)
  const agreedWithPolicies = useAppSelector(hasAgreedWithPolicies)
  const withPolicies = useAppSelector(hasPolicies)
  const template = useAppSelector(isTemplate)
  const eventPreview = useAppSelector(isEventPreview)
  const event = useAppSelector(getEvent)
  const assignedTicketsEnabled = isAssignedTickets(event)
  const messages = getFormMessages(event)

  useEffect(() => {
    if (experiments.enabled(ExperimentNames.NavigateToCheckoutWithReservationId) && !!placedOrder?.orderNumber) {
      const url = `${window.location.href}&orderNumber=${placedOrder.orderNumber}`

      window.history.replaceState(window.history.state, null, url)
    }
  }, [placedOrder?.orderNumber])

  const getSteps = () =>
    CHECKOUT_STEPS.filter(step =>
      isStepVisible({
        step,
        assignedTicketsEnabled,
        invoice,
        giftCardPaymentDetails,
        eventPreview,
        hasPolicies: withPolicies,
        isTemplate: template,
      }),
    ).map(step => {
      const stepTitle = t(`mobile.checkout.step.${step}`)
      const buyerDetailsTitle = messages?.checkout?.title ?? stepTitle
      const title = step === FormStep.BuyerDetails ? buyerDetailsTitle : stepTitle

      return {
        key: step,
        title,
        completed: isStepCompleted({
          step,
          placedOrder,
          assignedTicketsEnabled,
          buyerDetails,
          validPaymentAdded,
          agreedWithPolicies,
          allTicketDetailsValid,
          hasPolicies: withPolicies,
        }),
        renderStep: renderStep(step),
        onEdit: handleStepEdit,
      }
    })

  const handleStepEdit = async (step: FormStep) => {
    editStep(step, validPaymentAdded)

    if (step === FormStep.TicketsDetails) {
      setExpandedTicketIndex(0)
    } else if (step === FormStep.Payment) {
      payment.current.expand()
    } else {
      setExpandedTicketIndex(-1)
    }
  }

  const renderStep = (step: FormStep) => {
    const onSubmit = (data: any) => {
      if (currentStep === FormStep.Payment) {
        return handlePaymentStep()
      } else {
        StatusScreensEntry.preload()
        submitCheckoutStep(data)
      }
    }

    switch (step) {
      case FormStep.BuyerDetails:
        return (editing: boolean) => <BuyerDetails editing={editing} onSubmit={onSubmit} />
      case FormStep.Policies:
        return (editing: boolean) => <Policies editing={editing} onSubmit={onSubmit} />
      case FormStep.TicketsDetails:
        return (editing: boolean) => <TicketsDetails editing={editing} onSubmit={onSubmit} />
      case FormStep.Payment:
        return (editing: boolean) => <Payment paymentRef={payment} editing={editing} onSubmit={onSubmit} />
      default:
        return () => null
    }
  }

  const handlePaymentStep = async () => {
    const validPaymentAdded = await payment.current.validate()

    if (validPaymentAdded) {
      paymentMethodSelected(payment.current.props.currentPaymentMethodId)
      await pay()
    } else {
      setValidPaymentAdded(validPaymentAdded)
    }

    handleNextStep()
  }

  const pay = async () => {
    if (payment.current) {
      const status = await payment.current.pay()

      // @ts-expect-error
      if (status.errorCode) {
        setValidPaymentAdded(false)
        await handleStepEdit(FormStep.Payment)
        isMobile && focusElement({selector: hookToAttributeSelector('pw-payment-error')})
      } else {
        placeOrderButtonClicked()
      }

      return status
    }
  }

  return children(getSteps(), currentStep, pay)
}
